import clsx from 'clsx'
import { useState } from 'react'
import {
  type RecoveredRecording,
  type RecoveryUploadPhase,
  discardRecovered,
  uploadRecovered,
} from '../rt/recovery'
import { Panel } from './slp/Panel'
import { SlpButton } from './slp/SlpButton'

/**
 * Crash-recovery card (rt UI v2 — closes the S5 A6 gap): on join, surviving
 * OPFS .wav files from a previous session are offered for re-upload through
 * the frozen take pipeline, or discard.
 */

type RowState =
  | { phase: 'idle' }
  | { phase: RecoveryUploadPhase; pct: number }
  | { phase: 'discarded' }
  | { phase: 'error'; message: string }

function fmtMb(bytes: number): string {
  return `${(bytes / 1_048_576).toFixed(1)} MB`
}

export function RecoveryCard({
  files,
  linkToken,
  className,
}: {
  files: RecoveredRecording[]
  linkToken: string
  className?: string
}) {
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const setRow = (name: string, s: RowState) => setRows((cur) => ({ ...cur, [name]: s }))

  const visible = files.filter((f) => rows[f.fileName]?.phase !== 'discarded')
  if (visible.length === 0) return null

  const onUpload = async (rec: RecoveredRecording) => {
    setRow(rec.fileName, { phase: 'registering', pct: 0 })
    try {
      await uploadRecovered(linkToken, rec, (phase, pct) => setRow(rec.fileName, { phase, pct }))
    } catch (err) {
      setRow(rec.fileName, {
        phase: 'error',
        message: err instanceof Error ? err.message : 'upload failed',
      })
    }
  }

  const onDiscard = async (rec: RecoveredRecording) => {
    await discardRecovered(rec).catch(() => undefined)
    setRow(rec.fileName, { phase: 'discarded' })
  }

  return (
    <Panel title="Recovered Recordings" className={className}>
      <div data-testid="rt-recovery-card">
        <p className="mb-2 text-[12px] text-[var(--slp-text-dim)]">
          上次会话有未上传的录音 — recordings from a previous session survived in
          browser storage. Re-upload them or discard.
        </p>
        <ul className="flex flex-col gap-1.5">
          {visible.map((f) => {
            const st = rows[f.fileName] ?? { phase: 'idle' as const }
            const busy = st.phase === 'registering' || st.phase === 'uploading'
            return (
              <li
                key={f.fileName}
                data-testid="rt-recovery-item"
                data-status={st.phase}
                className="rounded border border-[var(--slp-border)] bg-[var(--slp-panel-2)] px-2 py-1.5"
              >
                <div className="truncate font-mono text-[11px] text-[var(--slp-text)]">{f.fileName}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--slp-text-dim)]">
                  <span>{fmtMb(f.bytes)}</span>
                  {f.durationSec != null ? <span>{f.durationSec.toFixed(1)} s</span> : null}
                  {f.take != null ? <span>take {f.take}</span> : null}
                  <span
                    className={clsx(
                      'ml-auto font-medium',
                      st.phase === 'uploaded' && 'text-[var(--slp-lamp-green)]',
                      st.phase === 'error' && 'text-[var(--slp-lamp-red)]',
                    )}
                  >
                    {st.phase === 'idle'
                      ? ''
                      : st.phase === 'uploaded'
                        ? '✓ uploaded'
                        : st.phase === 'error'
                          ? st.message
                          : st.phase === 'uploading'
                            ? `⬆ ${st.pct}%`
                            : 'registering…'}
                  </span>
                </div>
                {st.phase !== 'uploaded' ? (
                  <div className="mt-1.5 flex gap-2">
                    <SlpButton
                      size="sm"
                      data-testid="rt-recovery-upload"
                      disabled={busy}
                      onClick={() => void onUpload(f)}
                    >
                      补传 Upload
                    </SlpButton>
                    <SlpButton
                      size="sm"
                      variant="secondary"
                      data-testid="rt-recovery-discard"
                      disabled={busy}
                      onClick={() => void onDiscard(f)}
                    >
                      丢弃 Discard
                    </SlpButton>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      </div>
    </Panel>
  )
}
